import React, { useEffect, useRef, useState } from 'react';
import { DeckIcon } from '../deckIcons.jsx';
import { assignHands, pyRepr } from '../../lib/handPose.js';
import { stepHand } from '../../lib/handTrack.js';
import { simHandLandmarks } from '../../lib/simHand.js';
import { createErrorGate } from '../../lib/trackGuard.js';
import { useHandLandmarker } from './useHandLandmarker.js';
import { useSimInput } from './simInput.js';
import './camtags.css';

const BONES = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20], [0, 17],
];
const GAP_TEXT_MS = 160; // how often the gap's printed value may change
const CAMERA_WAIT_MS = 8000; // an unanswered permission prompt must not hold the slide
const DETECT_FAIL_LIMIT = 15; // detection errors in a row before the slide gives up

/**
 * The lesson's opening: the webcam with a name tag hanging off each of the
 * instructor's hands. Substep 0 counts fingers (left, right, total), 1 adds
 * the pinch gap in centimetres, 2 asks why one is never a decimal and the
 * other always is. The `reveal` copy at the start of part 2 shows every
 * value's type.
 *
 * The camera is opened only on the projector. With no camera, or no model
 * within 8 s, the slide switches to two drawn hands the instructor drives
 * from the keyboard — the story is the same.
 */
export default function CamTagsStop({ stop, substep = 0, preview }) {
  if (preview) {
    return (
      <section className="stop camtags-preview">
        <div className="stop-preview-stub">
          <DeckIcon name="monitor" size={44} />
          <p>{stop.title}</p>
          <span>กล้องเปิดบนจอฉายเท่านั้น</span>
        </div>
      </section>
    );
  }
  return <CamTags stop={stop} substep={substep} />;
}

function useCamera(enabled, videoRef) {
  const [state, setState] = useState({ status: enabled ? 'starting' : 'off', aspect: 16 / 9 });

  useEffect(() => {
    if (!enabled) return undefined;
    let stream = null;
    let cancelled = false;
    const off = () => !cancelled && setState({ status: 'off', aspect: 16 / 9 });
    // A permission prompt nobody answers, or a camera that never delivers,
    // counts as no camera: the slide moves on to its demo.
    const timer = setTimeout(() => {
      setState((s) => (s.status === 'starting' ? { status: 'off', aspect: 16 / 9 } : s));
    }, CAMERA_WAIT_MS);

    if (!navigator.mediaDevices?.getUserMedia) {
      off();
    } else {
      navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: false,
      }).then(
        async (s) => {
          if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
          stream = s;
          // A camera unplugged or taken by another app mid-lesson.
          s.getVideoTracks().forEach((t) => t.addEventListener('ended', off));
          const video = videoRef.current;
          video.srcObject = s;
          await video.play().catch(() => {});
          if (cancelled) return;
          const aspect = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
          setState((prev) => (prev.status === 'off' ? prev : { status: 'on', aspect }));
        },
        off
      );
    }
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [enabled, videoRef]);

  return state;
}

function useTracking(videoRef, landmarker, enabled) {
  const [hands, setHands] = useState({ left: null, right: null });
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    if (!enabled || !landmarker) return undefined;
    setBroken(false);
    let raf = 0;
    let lastTime = -1;
    let frame = 0;
    const state = { left: null, right: null };
    const gate = createErrorGate(DETECT_FAIL_LIMIT);
    const tick = () => {
      const video = videoRef.current;
      if (video && video.readyState >= 2 && video.currentTime !== lastTime) {
        lastTime = video.currentTime;
        let res;
        try {
          res = landmarker.detectForVideo(video, performance.now());
          gate.ok();
        } catch {
          // One bad frame is skipped; a run of them means the tracker is
          // gone (e.g. the GPU context was lost), and the demo takes over.
          if (gate.fail()) {
            setBroken(true);
            return;
          }
          raf = requestAnimationFrame(tick);
          return;
        }
        const found = (res.landmarks || []).map((landmarks, i) => ({
          landmarks,
          worldLandmarks: res.worldLandmarks?.[i] || null,
        }));
        const { left, right } = assignHands(found);
        state.left = stepHand(state.left, left);
        state.right = stepHand(state.right, right);
        frame += 1;
        if (frame % 2 === 0) setHands({ left: state.left, right: state.right });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled, landmarker, videoRef]);

  return { hands, broken };
}

/** Keeps a fast-changing number still long enough to read aloud. */
function useSteadyText(value) {
  const [text, setText] = useState(value);
  const at = useRef(0);
  useEffect(() => {
    const now = performance.now();
    if (now - at.current >= GAP_TEXT_MS || value == null || text == null) {
      at.current = now;
      setText(value);
      return undefined;
    }
    const t = setTimeout(() => {
      at.current = performance.now();
      setText(value);
    }, GAP_TEXT_MS);
    return () => clearTimeout(t);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return text;
}

function CamTags({ stop, substep }) {
  const reveal = Boolean(stop.reveal);
  const showGap = reveal || substep >= 1;
  const showQuestion = !reveal && substep >= 2;

  const videoRef = useRef(null);
  const model = useHandLandmarker({ enabled: true });
  const [camWanted, setCamWanted] = useState(true);
  const camera = useCamera(camWanted, videoRef);

  const { sim, demoAsked } = useSimInput(true);
  const [trackBroken, setTrackBroken] = useState(false);

  const mode = demoAsked || trackBroken || camera.status === 'off' || model.status === 'failed'
    ? 'sim'
    : camera.status === 'on' && model.status === 'ready' ? 'live' : 'starting';

  // A demo on a blank stage, not drawn hands over a live picture.
  useEffect(() => {
    if (mode === 'sim' && camWanted) setCamWanted(false);
  }, [mode, camWanted]);

  const tracking = useTracking(videoRef, model.landmarker, mode === 'live');
  const live = tracking.hands;
  // A tracker that keeps failing: on the GPU, retry once on the CPU; after
  // that, the demo.
  useEffect(() => {
    if (!tracking.broken) return;
    if (model.delegate === 'GPU') model.demote();
    else setTrackBroken(true);
  }, [tracking.broken]); // eslint-disable-line react-hooks/exhaustive-deps

  const hands = mode === 'sim'
    ? {
      left: { landmarks: simHandLandmarks({ fingers: sim.left, side: 'left' }), count: sim.left, gap: null },
      // Pinching, the thumb and index are busy: the demo hand counts on
      // middle, ring and pinky, so the tag never says more than it shows.
      right: {
        landmarks: simHandLandmarks({ fingers: sim.right, side: 'right', pinchCm: showGap ? sim.gap : null }),
        count: showGap ? Math.min(sim.right, 3) : sim.right,
        gap: sim.gap,
      },
    }
    : live;

  const leftN = hands.left?.count ?? 0;
  const rightN = hands.right?.count ?? 0;
  const measurer = hands.right || hands.left;
  const gapValue = measurer?.gap ?? null;
  const gapText = useSteadyText(gapValue == null ? null : pyRepr(gapValue, 'float'));
  const aspect = mode === 'live' ? camera.aspect : 16 / 9;

  return (
    <section
      className="stop camtags"
      data-mode={mode}
      data-camera={camera.status}
      data-model={model.status}
      data-delegate={model.delegate || ''}
      data-why={demoAsked ? 'asked' : trackBroken ? 'tracker' : ''}
    >
      <div className="camtags-video-col">
        <div className="camtags-stage" style={{ aspectRatio: aspect }}>
          <video ref={videoRef} className="camtags-video" playsInline muted />
          {mode !== 'starting' && (
            <Overlay
              hands={hands}
              aspect={aspect}
              showGap={showGap}
              reveal={reveal}
              leftN={leftN}
              rightN={rightN}
              measurer={measurer}
              gapText={gapText}
            />
          )}
          {mode === 'starting' && <p className="camtags-status">กำลังเปิดกล้อง…</p>}
          {mode === 'sim' && <span className="camtags-demo-chip">โหมดสาธิต</span>}
        </div>
      </div>

      <div className="camtags-panel">
        <header>
          {stop.kicker && <span className="stop-kicker">{stop.kicker}</span>}
          <h1 className="stop-title camtags-title">{stop.title}</h1>
        </header>

        <div className="code-panel camtags-code">
          <Line name="left" value={String(leftN)} kind="int" reveal={reveal} />
          <Line name="right" value={String(rightN)} kind="int" reveal={reveal} />
          <div><span>total</span><span className="op"> = </span>left<span className="op"> + </span>right</div>
          <div>print(total)</div>
          <div className="out">{leftN + rightN}</div>
          {showGap && <Line name="gap_cm" value={gapText ?? '…'} kind="float" reveal={reveal} />}
        </div>

        {showQuestion && <p className="camtags-question">{stop.question}</p>}
        {reveal && (
          <p className="camtags-rule">
            นับ <span className="op">→</span> <span className="type-word is-int">int</span>
            <span className="camtags-rule-gap" />
            วัด <span className="op">→</span> <span className="type-word is-float">float</span>
          </p>
        )}
      </div>
    </section>
  );
}

function Line({ name, value, kind, reveal }) {
  return (
    <div>
      <span>{name}</span>
      <span className="op">{' '.repeat(Math.max(1, 7 - name.length))}= </span>
      <span className={`is-${kind}`}>{value}</span>
      {reveal && <span className={`camtags-comment is-${kind}`}>{'   # '}{kind}</span>}
    </div>
  );
}

/* The skeleton, the tags and the ruler, drawn over the video. Landmarks are
   in image coordinates; the video is mirrored, so screen x = 1 - x. */
function Overlay({ hands, aspect, showGap, reveal, leftN, rightN, measurer, gapText }) {
  const W = aspect * 100;
  const sx = (p) => (1 - p.x) * W;
  const sy = (p) => p.y * 100;

  const tagSpot = (hand, fallbackX) => {
    if (!hand) return { left: `${fallbackX}%`, top: '14%' };
    const xs = hand.landmarks.map((p) => 1 - p.x);
    const ys = hand.landmarks.map((p) => p.y);
    const x = xs.reduce((a, b) => a + b, 0) / xs.length;
    return { left: `${x * 100}%`, top: `${Math.max(0.1, Math.min(...ys) - 0.04) * 100}%` };
  };

  const pinch = showGap && measurer ? [measurer.landmarks[4], measurer.landmarks[8]] : null;
  // The gap tag hangs beside the pinch on the side facing the middle of the
  // screen, so it never runs off the edge.
  let gapSpot = null;
  let gapSide = 'right';
  if (pinch) {
    const midX = 1 - (pinch[0].x + pinch[1].x) / 2;
    gapSide = midX > 0.5 ? 'left' : 'right';
    gapSpot = {
      left: `${(midX + (gapSide === 'left' ? -0.04 : 0.04)) * 100}%`,
      top: `${((pinch[0].y + pinch[1].y) / 2) * 100}%`,
    };
  }

  return (
    <>
      <svg className="camtags-svg" viewBox={`0 0 ${W} 100`} preserveAspectRatio="none" aria-hidden="true">
        {[hands.left, hands.right].filter(Boolean).map((h, k) => (
          <g key={k} className="camtags-bones">
            {BONES.map(([a, b], i) => (
              <line key={i} x1={sx(h.landmarks[a])} y1={sy(h.landmarks[a])} x2={sx(h.landmarks[b])} y2={sy(h.landmarks[b])} />
            ))}
            {h.landmarks.map((p, i) => <circle key={i} cx={sx(p)} cy={sy(p)} r="0.7" />)}
          </g>
        ))}
        {pinch && (
          <line className="camtags-ruler" x1={sx(pinch[0])} y1={sy(pinch[0])} x2={sx(pinch[1])} y2={sy(pinch[1])} />
        )}
      </svg>

      <HandTag name="left" value={leftN} spot={tagSpot(hands.left, 25)} absent={!hands.left} reveal={reveal} />
      <HandTag name="right" value={rightN} spot={tagSpot(hands.right, 75)} absent={!hands.right} reveal={reveal} />
      {gapSpot && gapText && (
        <div className={`camtags-tag is-gap is-${gapSide}`} style={gapSpot}>
          <span className="nt"><span>gap_cm</span></span>
          <span className="val is-float">{gapText}</span>
          {reveal && <span className="type-word is-float camtags-type">float</span>}
        </div>
      )}
    </>
  );
}

function HandTag({ name, value, spot, absent, reveal }) {
  return (
    <div className={`camtags-tag ${absent ? 'is-absent' : ''}`} style={spot}>
      <span className="nt"><span>{name}</span></span>
      <span className="val is-int">{value}</span>
      {reveal && <span className="type-word is-int camtags-type">int</span>}
    </div>
  );
}
